/* ServiceNowRenderer.js */

"use strict";
/* global Slick */
const _ = require("lodash");
const uiCore = require("@gluenetworks/steve_ui_core");
const {ComboBox} = uiCore.Control.OptionSelection.Single;
const ImportScreenComponent = require("../veils/ImportExport/ImportExportScreen/ImportScreenComponent");
const ImportExportSummaryComponent = require("../veils/ImportExport/ImportExportSummary/ImportExportSummaryComponent");
const FileName = require("@gluenetworks/steve_utilities").fileName;
const ScheduleComponent = require("@gluenetworks/schedule_component");
require("@gluenetworks/jstree");
const formatters = require("../cellFormaters");
const fieldEditor = require("../cellEditors/FieldEditor");
const SelectBoxEditor = require("../cellEditors/SelectBoxEditor");
const SourceSelectEditor = require("../cellEditors/SourceSelectBoxEditor");
const FieldOrSelectEditor = require("../cellEditors/FieldOrSelectEditor");
const SelectTypeEditor = require("../cellEditors/SelectTypeEditor");
const ClassEditor = require("../cellEditors/ClassEditor");
const checkBoxEditor = require("../cellEditors/CheckBoxEditor");
const $ = require("jquery");
const CmdbNotificationComponent = require("../libs/CmdbNotificationComponent");

const {
    Control: {
        Action: {Button},
        Boolean: {InputCheckbox},
    },
} = uiCore;

const SERVICE_NOW_TABS = [
    {label: "Connection", value: "connections"},
    {label: "Class & Device Mapping", value: "classNDeviceMapping"},
    {label: "Device & CI Linking", value: "deviceCILinking"},
    {label: "Device & CI Syncing", value: "deviceCISyncing"},
    {label: "Linking & Syncing Schedule", value: "LinkingSyncing"},
];

/**
 * class CaptureRenderer
 *
 * @listens CaptureRenderer#event:DataUpdated
 *
 */
class ServiceNowViewRenderer {
    /**
     * @param {ServiceNowViewRenderer} model
     */
    constructor(model, uiUtils) {
        this._model = model;
        this._uiUtils = uiUtils;
        this._rootElem = document.createElement("cmdb-integration-gl");
        this._rootElem.component = this._model;
        this._fileNamer = FileName;
        this._activeTab = "connections";
        this._valueChange = this._valueChange.bind(this);
        this._isScheduleValid = true;

        this._defaultSchedule = ScheduleComponent.defaultValue({
            allowOne: false,
            allowRepeat: true,
            pollOnly: false,
            availOnly: true,
        });

        this._importButton = new Button({
            label: "Import",
            darkTheme: true,
        });

        this._exportButton = new Button({
            label: "Export",
            darkTheme: true,
            hidden: true,
        });

        this._cancelButton = new Button({
            label: "Cancel",
            darkTheme: true,
        });
        this._saveButton = new Button({
            label: "Save",
            darkTheme: true,
        });

        this._errContainer = document.createElement("span");
        this._errContainer.classList.add("error-container");

        this._tableOptions = {
            editable: true,
            enableCellNavigation: true,
            forceFitColumns: true,
            autoHeight: false,
            autoEdit: true,
            resizable: false,
            rowHeight: 40,
            enableAsyncPostRender: false,
            asyncPostRenderDelay: 0,
        };

        this._treeViewForClasses = {};

        /* User Data - Start */
        this._userDataStorage = this._model._storageFactory.getRemoteStorageInstance(
            "views",
            "servicenow-config"
        );
        /* User Data - End */
        this._handleConnectionsLinkClick = this._handleConnectionsLinkClick.bind(this);
        this._createConnectionsTab();
        this._createLinkingAndSyncingScheduleTab();
        this._createDeviceAndCiLinkingTab();
        this._createDeviceAndCiSyncingTab();
        this._createClassAndDeviceMappingTab();
    }

    /*******************************************/
    /* START GETTERS AND SETTERS                 */
    /*******************************************/

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END GETTERS AND SETTERS                 */
    /* START COMMON RENDER METHODS             */
    /*******************************************/

    render() {
        if (this._model) {
            return this._model
                .whenModelIsReady()
                .then(() => this._scheduleFrequencyComponent.renderer.render())
                .then(() => this._notificationComponent.renderer.render())
                .then(() => {
                    this._rootElem.innerHTML = "";
                    // this._rootElem.appendChild(this._headerElem);

                    this._rootElem.appendChild(this._enableServiceNow._renderer._rootElem);

                    this._enableServiceNow.setValue(
                        this._model._snDataSyncConfigEntity.getEnabled() || false
                    );

                    this._createTabs();

                    const _buttonContainer = document.createElement("div");
                    _buttonContainer.classList.add("btn-container");
                    const _leftBtnContainer = document.createElement("div");
                    _leftBtnContainer.classList.add("left-btn-container");
                    _leftBtnContainer.appendChild(this._importButton._renderer._rootElem);
                    _leftBtnContainer.appendChild(this._exportButton._renderer._rootElem);

                    const _rightBtnContainer = document.createElement("div");
                    _rightBtnContainer.classList.add("right-btn-container");
                    _rightBtnContainer.appendChild(this._cancelButton._renderer._rootElem);
                    _rightBtnContainer.appendChild(this._saveButton._renderer._rootElem);

                    _buttonContainer.appendChild(_leftBtnContainer);
                    _buttonContainer.appendChild(_rightBtnContainer);

                    this._rootElem.appendChild(this._errContainer);
                    this._rootElem.appendChild(_buttonContainer);
                    if (this._model._noVendorMap) {
                        this._noVendorMaps();
                    }

                    return Promise.resolve();
                })
                .then(() => {
                    this._onTabClickEvent(undefined, "connections");

                    this._fitTableSize();

                    if (this._model._cmdbConnectionFailed || !this._enableServiceNow.getValue()) {
                        this._failedCMDBConnect();
                    } else {
                        this._toggleConnectionStatus(true);
                    }
                })
                .catch((err) => console.error(err) || Promise.reject(err));
        }
    }

    _createTabs() {
        this._tabParent = document.createElement("div");
        this._tabParent.classList.add("tabs");
        this._tabParent.classList.add("service-now-tabs");

        const _tabContentArr = [];

        this._tabElements = {};

        SERVICE_NOW_TABS.forEach((item) => {
            const _tabAction = document.createElement("button");
            this._tabElements[item.value] = _tabAction;
            _tabAction.classList.add("tab-btn");
            _tabAction.innerHTML = item.label;

            if (item.value === "connections") {
                _tabAction.appendChild(this._onlineIcon);
                _tabAction.appendChild(this._offlineIcon);
            }

            _tabAction.setAttribute("data-type", item.value);

            _tabAction.addEventListener(
                "click",
                (this._onTabClickEvent = this._onTabClickEvent.bind(this))
            );

            this._tabParent.appendChild(_tabAction);

            const _tabContent = document.createElement("div");
            _tabContent.classList.add("tab-content");
            _tabContent.setAttribute("data-type", item.value);

            _tabContent.appendChild(this._getTabContent(item.value));

            _tabContentArr.push(_tabContent);
        });

        this._rootElem.appendChild(this._tabParent);

        _tabContentArr.forEach((item) => {
            this._rootElem.appendChild(item);
        });

        const connectionComboBoxValue =
            this._model._snDataSyncConfigEntity.serviceConnectionId ||
            (this._model._serviceConnectionEntities[0] &&
                this._model._serviceConnectionEntities[0].id) ||
            null;
        this._connectionComboBox.setValue(connectionComboBoxValue);

        if (this._model._hasConnections) {
            this._noConnectionEl.classList.remove("show");
            this._connectionsTabContent.classList.remove("hidden");
        } else {
            this._noConnectionEl.classList.add("show");
            this._connectionsTabContent.classList.add("hidden");
        }

        this._valueChange();
        // this._validateCMDBSyncIntegrations(); // called from value change

        this._disableEnableTabs(!this._enableServiceNow.getValue());

        this._registerListeners();
    }

    /*******************************************/
    /* END COMMON RENDER METHODS               */
    /* START REST OF THE TABS                  */
    /*******************************************/

    /*******************************************/
    /* END REST OF THE TABS                    */
    /* START COMMON ENTITY METHODS             */
    /*******************************************/

    _setEntity() {
        this._model._serviceConnectionEntity = this._getConnectionInformation();
        this._model._snDataSyncConfigEntity.setServiceConnectionId(
            _.get(this._model._serviceConnectionEntity, "id", null)
        );

        this._model._snDataSyncConfigEntity.setDataProvider("serviceNow");
        this._model._snDataSyncConfigEntity.setDataProviderProduct("cmdb");

        this._model._snDataSyncConfigEntity.setScheduleEnabled(
            this._enableDisableSchedulesCheckbox.getValue() || false
        );
        this._model._snDataSyncConfigEntity.setSchedule(this._getSchedule());
        const notifWorkInp = this._notificationComponent.getNotificationsWorkInputObject();
        if (
            !notifWorkInp.notifyMe &&
            !_.get(
                this._model._snDataSyncConfigEntity,
                "pristineProperties.scheduleWorkInput.notifyMe"
            )
        ) {
            notifWorkInp.notifyMe = _.get(
                this._model._snDataSyncConfigEntity,
                "pristineProperties.scheduleWorkInput.notifyMe"
            );
        }
        const scheduleWorkInput = Object.assign({}, notifWorkInp, {
            dataProvider: "serviceNow",
            dataProviderProduct: "cmdb",
        });
        this._model._snDataSyncConfigEntity.setScheduleWorkInput(scheduleWorkInput);
    }

    /*******************************************/
    /* END COMMON ENTITY METHODS               */
    /* START VALIDATION METHODS                */
    /*******************************************/

    _addRemoveTabErrorClass(elem, isValid) {
        if (isValid) {
            elem.classList.remove("errored");
        } else {
            elem.classList.add("errored");
        }
    }

    _validateCMDBSyncIntegrations() {
        if (this._validateConnectionTab()) {
            this._testButton.setDisabled(false);
        } else {
            this._testButton.setDisabled(true);
        }

        const isDeviceCILinkingTabValid = this._validatedeviceCILinkingTab();
        this._addRemoveTabErrorClass(
            this._tabElements["deviceCILinking"],
            isDeviceCILinkingTabValid
        );
        this._showErrorMessage("deviceCILinking");

        const isDeviceCISyncingTabValid = this._validatedeviceCISyncingTab();
        this._addRemoveTabErrorClass(
            this._tabElements["deviceCISyncing"],
            isDeviceCISyncingTabValid
        );
        this._showErrorMessage("deviceCISyncing");

        const isValid =
            this._validateClassNDeviceMappingTab() &&
            isDeviceCILinkingTabValid &&
            isDeviceCISyncingTabValid &&
            this._validateLinkingSyncingTab();

        if (!isValid) {
            this._enableDisableSaveBtn(true);
        } else {
            if (this._model._snDataSyncConfigEntity.isDirty()) {
                this._enableDisableSaveBtn(false);
            } else {
                this._enableDisableSaveBtn(true);
            }
        }
    }

    _validateClassNDeviceMappingTab() {
        return true;
    }
    _validatedeviceCILinkingTab() {
        return !this._model._isDnCLErrored;
    }
    _validatedeviceCISyncingTab() {
        return !this._model._isDnCSErrored;
    }

    /*******************************************/
    /* END VALIDATION METHODS               */
    /* START COMMON HELPER METHODS             */
    /*******************************************/

    _getTabContent(tabType) {
        switch (tabType) {
            case "connections":
                return this._getConnectionsContent();
            case "classNDeviceMapping":
                return this._getclassNDeviceMappingContent();
            case "deviceCILinking":
                return this._getdeviceCILinkingContent();
            case "deviceCISyncing":
                return this._getdeviceCISyncingContent();
            case "LinkingSyncing":
                return this._getLinkingSyncingContent();
        }
    }

    _disableEnableTabs(state) {
        this._connectionComboBox.setDisabled(state);
        const tabsContainer = this._rootElem.querySelector(".tabs"),
            tabsContentContainers = this._rootElem.querySelectorAll(".tab-content");
        if (state) {
            tabsContainer.classList.add("disabled");
            tabsContentContainers.forEach((tabsContentContainer) => {
                tabsContentContainer.classList.add("disabled");
            });
        } else {
            tabsContainer.classList.remove("disabled");
            tabsContentContainers.forEach((tabsContentContainer) => {
                tabsContentContainer.classList.remove("disabled");
            });
        }
    }

    _enableDisableSaveBtn(val) {
        this._saveButton.setDisabled(val);
        if (this._model._snDataSyncConfigEntity.isDirty()) {
            this._cancelButton.setDisabled(false);
        } else {
            this._cancelButton.setDisabled(true);
        }
    }

    _showError(error) {
        if (_.get(error, "errorCode") !== "NoPendingUpdates") {
            this._uiUtils.showMessageDialog({
                message: "An error occurred: " + this._uiUtils.errorCodeMapping(error),
                confirmButtonName: "OK",
            });
        }
    }

    _showErrorMessage(tab) {
        switch (tab) {
            case "deviceCILinking":
                if (this._activeTab === tab) {
                    this._errContainer.innerText = "";
                    if (!this._validatedeviceCILinkingTab()) {
                        this._errContainer.innerText =
                            "Error - Type mismatch. Synced properties must use the matching data types.";
                    }
                }
                break;
            case "deviceCISyncing":
                if (this._activeTab === tab) {
                    this._errContainer.innerText = "";
                    if (!this._validatedeviceCISyncingTab()) {
                        this._errContainer.innerText =
                            "Error - Type mismatch. Synced properties must use the matching data types.";
                    }
                }
                break;
            default:
                this._errContainer.innerText = "";
                break;
        }
    }

    shutdownRenderer() {
        const promises = [
            this._getElementShutdownIfExists(this._rootElem),
            this._getElementShutdownIfExists(this._importButton),
            this._getElementShutdownIfExists(this._exportButton),
            this._getElementShutdownIfExists(this._cancelButton),
            this._getElementShutdownIfExists(this._saveButton),
            this._getElementShutdownIfExists(this._enableServiceNow),
            this._getElementShutdownIfExists(this._urlTextBox),
            this._getElementShutdownIfExists(this._authMethod),
            this._getElementShutdownIfExists(this._clientIdTextBox),
            this._getElementShutdownIfExists(this._clientSecretCmbBox),
            this._getElementShutdownIfExists(this._userTextBox),
            this._getElementShutdownIfExists(this._passCredCmbBox),
            this._getElementShutdownIfExists(this._testButton),
            this._getElementShutdownIfExists(this._addNewConnectionButton),
            this._getElementShutdownIfExists(this._enableDisableSchedulesCheckbox),
            this._getElementShutdownIfExists(this._scheduleFrequencyComponent),
            this._getElementShutdownIfExists(this._previewButton),
            this._getElementShutdownIfExists(this._runButton),
            this._getElementShutdownIfExists(this._downloadButton),
            this._getElementShutdownIfExists(this._addNewRowButton),
            this._getElementShutdownIfExists(this._addNewRowDnCSButton),
            this._getElementShutdownIfExists(this._rootElem),
            this._getElementShutdownIfExists(this._addNewRowCnDMButton),
        ];

        return Promise.all(promises).then(() => {
            [
                "_rootElem",
                "_fileNamer",
                // "_headerElem",
                "_valueChange",
                "_defaultSchedule",
                "_importButton",
                "_exportButton",
                "_cancelButton",
                "_errContainer",
                "_saveButton",
                "_tabParent",
                /* Connection Tab : Start */
                "_enableServiceNow",
                "_urlTextBox",
                "_authMethod",
                "_clientIdTextBox",
                "_clientSecretCmbBox",
                "_userTextBox",
                "_passCredCmbBox",
                "_activeTab",
                "_testButton",
                "_addNewConnectionButton",
                "_offlineIcon",
                "_onlineIcon",
                /* Connection Tab : End */

                /* Linking And Syncing Schedule : Start */
                "_enableDisableSchedulesCheckbox",
                "_scheduleFrequencyComponent",
                "_previewButton",
                "_runButton",
                "_downloadButton",
                "_lastSyncingMsg",
                "scheduleButtonContainer",
                /* Linking And Syncing Schedule : End */

                /* Device And Ci Linking : Start */
                "_deviceAndCiLinkingContainer",
                "_treeClassesCont",
                "_addNewRowButton",
                "_slickTableDnCLContainer",
                /* Device And Ci Linking : End */

                /* Device And Ci Syncing : Start */
                "_deviceAndCiSyncingContainer",
                "_treeClassesCont",
                "_addNewRowDnCSButton",
                "_slickTableDnCSContainer",
                "_slickTableDnCSDerivedContainer",
                /* Device And Ci Syncing : End */

                /* Ci And Device Mapping : Start */
                "_onCnDMRowCountChange",
                "_onCnDMCellChangeAction",
                "_onCnDMRowsChanged",
                "_classAndDeviceMappingContainer",
                "_addNewRowCnDMButton, /" / "_slickTableCnDMContainer",
                "_slickCnDMTable",
                "_maxSortOrderCnDM",
                "_columnsCnDM",
                "_slickCnDMDataVIew",
                /* Ci And Device Mapping : End */

                /* Slick Items */
                "_tableOptions",
                "_slickDnCLTable",
                "_maxSortOrderDnCL",
                "_columnsDnCL",
                "_slickDnCSTable",
                "_slickDnCSDerivedTable",
                "_maxSortOrderDnCS",
                "_maxSortOrderDnCSDerived",
                "_columnsDnCS",
                "_columnsDnCSDerived",
                /* Slick Items */

                /* Tree Items */
                "_treeViewForClasses",
                "_treeViewForClasses2",
                /* Tree Items */
            ].forEach((elem) => (this[elem] = undefined));

            return Promise.resolve();
        });
    }

    _getElementShutdownIfExists(elem) {
        return !_.isEmpty(elem) && elem.shutdown ? elem.shutdown() : Promise.resolve();
    }

    _entityContainsMapping() {
        if (_.isEmpty(this._model._snDataSyncConfigEntity.getNamespaceMatchingCriteria())) {
            this._rootElem.querySelectorAll(".tab-content").forEach((node) => {
                if (
                    ["deviceCILinking", "deviceCISyncing"].indexOf(
                        node.getAttribute("data-type") || " "
                    ) > -1
                ) {
                    node.querySelector(".no-mapping").classList.add("show");
                }
            });
        } else {
            this._rootElem.querySelectorAll(".tab-content").forEach((node) => {
                if (
                    ["deviceCILinking", "deviceCISyncing"].indexOf(
                        node.getAttribute("data-type") || " "
                    ) > -1
                ) {
                    node.querySelector(".no-mapping").classList.remove("show");
                }
            });
        }
    }

    /*******************************************/
    /* END COMMON HELPER METHODS               */
    /* START COMMON EVENT HANDLER METHODS      */
    /*******************************************/
    _registerNotificationComponentListeners() {
        this._notificationComponent.on(
            "ValidationStateChanged",
            (this._onValidationStateChanged = this._onValidationStateChanged.bind(this))
        );
        this._notificationComponent.on("EmailListWasChanged", this._valueChange);
    }

    _unRegisterNotificationComponentListeners() {
        this._notificationComponent.removeListener(
            "ValidationStateChanged",
            this._onValidationStateChanged
        );
        this._notificationComponent.removeListener("EmailListWasChanged", this._valueChange);
    }

    _registerListeners() {
        // Generic Listeners : Start
        window.addEventListener("resize", (this._fitTableSize = this._fitTableSize.bind(this)));
        document.addEventListener("click", (this._clickHandler = this._clickHandler.bind(this)));
        this._saveButton.on(
            "UserClick",
            (this._saveButtonAction = this._saveButtonAction.bind(this))
        );
        this._cancelButton.on(
            "UserClick",
            (this._cancelButtonAction = this._cancelButtonAction.bind(this))
        );

        this._importButton.on("UserClick", (this._importAction = this._importAction.bind(this)));
        this._exportButton.on("UserClick", (this._exportAction = this._exportAction.bind(this)));
        this._model.on("OrgChange", (this._orgChange = this._orgChange.bind(this)));
        this._model.on("ResetAllValues", (this._resetAllValues = this._resetAllValues.bind(this)));
        this._model.on(
            "FailedCMDBConnect",
            (this._failedCMDBConnect = this._failedCMDBConnect.bind(this))
        );
        this._model.on(
            "SavedWithGoodConnection",
            (this._savedWithGoodConnection = this._savedWithGoodConnection.bind(this))
        );
        this._model.on(
            "FailedCMDBConnect",
            (this._failedCMDBConnect = this._failedCMDBConnect.bind(this))
        );
        this._model.on(
            "ConnectionSuccessful",
            (this._connectionSuccessful = this._connectionSuccessful.bind(this))
        );
        this._model.on(
            "ConnectionFailed",
            (this._connectionFailed = this._connectionFailed.bind(this))
        );
        this._model.on("NoVendorMap", (this._noVendorMaps = this._noVendorMaps.bind(this)));
        // this._model.on("SavedWithBadConnection", this._savedWithBadConnection = this._savedWithGoodConnection.bind(this));

        // Generic Listeners : End

        // Connection Tab Listeners : Start
        this._enableServiceNow.on(
            "UserValue",
            (this._enableChange = this._enableChange.bind(this))
        );
        this._connectionComboBox.on("UserValue", this._valueChange);
        this._testButton.on(
            "UserClick",
            (this._testSSConnectivityAction = this._testSSConnectivityAction.bind(this))
        );

        this._addNewConnectionButton.on(
            "UserClick",
            (this._newConnectionAction = this._newConnectionAction.bind(this))
        );

        this._connectionsLinkEl.addEventListener("click", this._handleConnectionsLinkClick);
        // Connection Tab Listeners : End

        // Schedule Tab Listeners : Start
        this._enableDisableSchedulesCheckbox.on("UserValue", this._valueChange);
        this._scheduleFrequencyComponent.on("UserValue", this._valueChange);
        this._previewButton.on("UserClick", (this._previewWork = this._previewWork.bind(this)));
        this._runButton.on("UserClick", (this._runWork = this._runWork.bind(this)));
        this._downloadButton.on(
            "UserClick",
            (this._downloadLastWork = this._downloadLastWork.bind(this))
        );
        this._registerNotificationComponentListeners();
        // Schedule Tab Listeners : End

        // Device & CI Linking Tab : Start
        this._model.on(
            "RefreshDeviceAndCiLinkingGrid",
            (this.resetDnCLSlickTable = this.resetDnCLSlickTable.bind(this))
        );

        this._addNewRowButton.on(
            "UserClick",
            (this._addD2CLinkingClick = this._addD2CLinkingClick.bind(this))
        );
        this._enableNewCI.on(
            "UserValue",
            (this._allowNewCiChange = this._allowNewCiChange.bind(this))
        );

        this._model.on("ErrorInLinking", (this._errorInLinking = this._errorInLinking.bind(this)));
        // Device & CI Linking Tab : End

        // Device & CI Syncing Tab : Start
        this._addNewRowDnCSButton.on(
            "UserClick",
            (this._addD2CSyncingClick = this._addD2CSyncingClick.bind(this))
        );
        this._model.on(
            "RefreshDeviceAndCiSyncingGrid",
            (this.resetDnCSSlickTable = this.resetDnCSSlickTable.bind(this))
        );
        this._model.on("ErrorInSyncing", (this._errorInSyncing = this._errorInSyncing.bind(this)));
        // Device & CI Syncing Tab : End

        /* Class & Device Mapping Tab : Start */
        this._model.on(
            "RefreshClassAndDeviceMappingGrid",
            (this.resetCnDMSlickTable = this.resetCnDMSlickTable.bind(this))
        );
        this._addNewRowCnDMButton.on(
            "UserClick",
            (this._addCnDMappingClick = this._addCnDMappingClick.bind(this))
        );

        /* Class & Device Mapping Tab : End */
    }

    _unregisterListeners() {
        // Generic Listeners : Start
        window.removeEventListener("resize", this._fitTableSize);
        document.removeEventListener("click", this._clickHandler);
        if (this._saveButton) {
            this._saveButton.removeListener("UserClick", this._saveButtonAction);
        }
        if (this._importButton) {
            this._importButton.removeListener("UserClick", this._importAction);
        }
        if (this._exportButton) {
            this._exportButton.removeListener("UserClick", this._exportAction);
        }
        this._model.removeListener("OrgChange", this._orgChange);
        this._model.removeListener("ResetAllValues", this._resetAllValues);
        this._model.removeListener("FailedCMDBConnect", this._failedCMDBConnect);
        this._model.removeListener("SuccessfulCMDBConnect", this._successfulCMDBConnect);
        this._model.removeListener("SavedWithGoodConnection", this._savedWithGoodConnection);
        this._model.removeListener("ConnectionSuccessful", this._connectionSuccessful);
        this._model.removeListener("ConnectionFailed", this._connectionFailed);
        this._model.on("NoVendorMap", this._noVendorMaps);
        // Generic Listeners : End

        // Connection Tab Listeners : Start
        this._enableServiceNow.removeListener("UserValue", this._enableChange);
        this._connectionComboBox.removeListener("UserValue", this._valueChange);
        this._testButton.removeListener("UserClick", this._testSSConnectivityAction);
        this._addNewConnectionButton.removeListener("UserClick", this._newConnectionAction);
        this._connectionsLinkEl.removeEventListener("click", this._handleConnectionsLinkClick);
        // Connection Tab Listeners : End

        // Schedule Tab Listeners : Start
        this._enableDisableSchedulesCheckbox.removeListener("UserValue", this._valueChange);
        this._scheduleFrequencyComponent.removeListener("UserValue", this._valueChange);
        this._previewButton.removeListener("UserClick", this._previewWork);
        this._runButton.removeListener("UserClick", this._runWork);
        this._unRegisterNotificationComponentListeners();
        // Schedule Tab Listeners : End

        // Device & CI Linking Tab : Start
        this._model.removeListener("RefreshDeviceAndCiLinkingGrid", this.resetDnCLSlickTable);
        this._addNewRowButton.removeListener("UserClick", this._addD2CLinkingClick);
        this._enableNewCI.removeListener("UserValue", this._allowNewCiChange);
        this._model.removeListener("ErrorInLinking", this._errorInLinking);
        // Device & CI Linking Tab : End

        // Device & CI Syncing Tab : Start
        this._addNewRowDnCSButton.removeListener("UserClick", this._addD2CSyncingClick);
        this._model.removeListener("RefreshDeviceAndCiSyncingGrid", this.resetDnCSSlickTable);
        this._model.removeListener("ErrorInSyncing", this._errorInSyncing);
        // Device & CI Syncing Tab : End
    }

    shutdown() {
        this._unregisterListeners();
        this._unregisterDnCLSlickTableListeners();
        this._unregisterDnCLDerivedSlickTableListeners();
        this._unregisterDnCSSlickTableListeners();
        this._unregisterDnCSDerivedSlickTableListeners();
        this._unregisterCnDMSlickTableListeners();

        return Promise.resolve().then(() => {
            this._model = undefined;
            this._rootElem.remove();
            return this.shutdownRenderer();
        });
    }

    async _resetAllValues() {
        this._setConnectionValues();
        const scheduleWorkInput =
            this._model._snDataSyncConfigEntity.pristineProperties.scheduleWorkInput;
        this._notificationComponent.resetCmdbNotificationComponent({
            value: scheduleWorkInput,
        });
        await this._resetDnCLData(this._model._selectedClass || "cmdb_ci");
        await this._resetDnCSData(this._model._selectedClassSyncing || "cmdb_ci");
        await this._resetCnDMData();
    }

    _connectionSuccessful() {
        this._toggleConnectionStatus(true);
    }
    _connectionFailed() {
        this._toggleConnectionStatus(false);
    }

    _noVendorMaps() {
        this._rootElem.querySelectorAll(".tab-content").forEach((node) => {
            if (
                ["classNDeviceMapping", "deviceCILinking", "deviceCISyncing"].indexOf(
                    node.getAttribute("data-type") || " "
                ) > -1
            ) {
                node.querySelector(".no-vendor-map").classList.add("show");
            }
        });
    }

    _failedCMDBConnect() {
        this._rootElem.querySelectorAll(".tab-content").forEach((node) => {
            if (
                ["classNDeviceMapping", "deviceCILinking", "deviceCISyncing"].indexOf(
                    node.getAttribute("data-type") || " "
                ) > -1
            ) {
                node.querySelector(".no-cmdb-connection").classList.add("show");
            }
        });
        this._toggleConnectionStatus(false);
    }

    _successfulCMDBConnect() {
        this._rootElem.querySelectorAll(".tab-content").forEach((node) => {
            if (
                ["classNDeviceMapping", "deviceCILinking", "deviceCISyncing"].indexOf(
                    node.getAttribute("data-type") || " "
                ) > -1
            ) {
                node.querySelector(".no-cmdb-connection").classList.remove("show");
            }
        });
        this._toggleConnectionStatus(true);
    }

    async _savedWithGoodConnection() {
        this._successfulCMDBConnect();
        this.resetJSTrees();
        await this._resetAllValues();
    }

    _orgChange() {
        this._uiUtils
            .showMessageDialog({
                message: "You have changed organizations. The browser will be refreshed.",
                confirmButtonName: "OK",
            })
            .then(() => {
                this._model._router.reload();
            });
    }

    _enableChange(val) {
        if (val) {
            const msg = `Enabling this feature constitutes acceptance and understanding that Gluware will collect data from the configured ServiceNow CMDB. Gluware, Inc. is not liable or responsible for the accuracy, timeliness or usefulness of the data collected from this 3rd Party and presented in the Gluware application.`;
            return this._uiUtils
                .showMessageDialog({
                    message: msg,
                    confirmButtonName: "OK",
                })
                .then(() => {
                    this._model._snDataSyncConfigEntity.setEnabled(true);
                    return this._model._saveEntity();
                })
                .then(() => {
                    this._disableEnableTabs(false);
                    return Promise.resolve();
                });
        } else {
            this._model._snDataSyncConfigEntity.setEnabled(false);
            this._disableEnableTabs(true);
            return this._model._saveEntity();
        }
    }

    async _onTabClickEvent(event, passedType = undefined) {
        // event && event.preventDefault();
        const currentType = passedType ? passedType : event.currentTarget.getAttribute("data-type");
        this._activeTab = currentType;
        this._showErrorMessage(currentType);
        const tabs = document.querySelectorAll(".tab-btn");
        tabs.forEach((tabVal) => {
            tabVal.classList.remove("selected");

            if (currentType === tabVal.getAttribute("data-type")) {
                tabVal.classList.add("selected");
            }
        });

        // Hide all tab contents
        const tabContents = document.querySelectorAll(".tab-content");
        tabContents.forEach((tabContent) => {
            tabContent.classList.remove("show");

            if (currentType === tabContent.getAttribute("data-type")) {
                tabContent.classList.add("show");
            }
        });

        if (currentType === "deviceCILinking") {
            this._entityContainsMapping(); // Call this when the tab is clicked and show no mapping message if needed
            const selectedNode =
                this._userDataStorage.getData("servicenow-linking-selected-node", true) || "1";
            this._treeViewForClasses.jstree("select_node", selectedNode);
        } else if (currentType === "deviceCISyncing") {
            this._entityContainsMapping(); // Call this when the tab is clicked and show no mapping message if needed
            const selectedNode =
                this._userDataStorage.getData("servicenow-syncing-selected-node", true) || "1";
            this._treeViewForClasses2.jstree("select_node", selectedNode);
        } else if (currentType === "classNDeviceMapping") {
            this._renderCnDMSlickGrid();
            this._resetCnDMData();
        }
    }

    async _exportAction() {
        try {
            await this._model._saveEntity();
            const exportResult = await this._model._snDataSyncConfigEntity.runExportWork();

            this._model._muf.hideDisplayed();

            if (exportResult.summary.exportSuccessful) {
                const _fileName = this._fileNamer({
                    solution: "SS",
                    general: "CMDBIntegration",
                    specific: this._model._organizationData.name,
                });

                this._model._fileFactory.saveJsonAsFile(
                    _.get(exportResult, "summary.export"),
                    _fileName + ".json"
                );
            }

            const exportSummaryScreen = new ImportExportSummaryComponent(
                exportResult,
                this._model._logger,
                "export"
            );
            await this._model._muf.displayCustom(exportSummaryScreen, {
                dontHide: true,
            });

            this._model._muf.hideDisplayed();
        } catch (err) {
            this._model._muf.hideDisplayed();
            this.model._logger.warn("ServiceNowViewRenderer._exportAction() error.");
        }
    }

    async _importAction() {
        try {
            let importScreenComponent = new ImportScreenComponent(
                this._model._logger,
                this._model._userSession,
                this._model._steveIo
            );
            const result = await this._model._muf.displayCustom(importScreenComponent, {
                dontHide: true,
            });

            const importFileProvider = _.get(result, "import.dataProvider");
            if (importFileProvider !== "serviceNow") {
                throw {
                    errorCode: "INVALID_IMPORT_FILE",
                    errorMsg:
                        "The uploaded import file is not a ServiceNow configuration file. Please check and try again.",
                    userRejected: false,
                };
            }

            const importResult = await this._model._snDataSyncConfigEntity.runImportWork(
                result,
                {}
            );

            const importSummaryScreen = new ImportExportSummaryComponent(
                importResult,
                this._model._logger,
                "import"
            );

            await this._model._muf.displayCustom(importSummaryScreen, {
                dontHide: true,
            });

            // reload data
            this._model._snDataSyncConfigEntity =
                await this._model._DataSyncConfigEntityDef.loadProviderConfig(
                    this._model.activeOrgId,
                    "serviceNow",
                    "cmdb"
                );
            this._resetAllValues();

            this._model._muf.hideDisplayed();
        } catch (err) {
            await this._model._muf.hideDisplayed();
            if (err.userRejected) {
                this.model._logger.warn("ServiceNowViewRenderer._importAction() Cancelled.");
                return;
            }
            const errObj = {
                errorCode: _.get(err, "errorCode", ""),
                errorMsg: _.get(err, "errorMsg", ""),
                title: "Error",
            };
            try {
                await this._model._muf.displayError(errObj);
            } catch (err) {
                this.model._logger.warn("ServiceNowViewRenderer._importAction() Error.");
                return;
            }

            this.model._logger.warn("ServiceNowViewRenderer._importAction() Error.");
        }
    }

    async _saveButtonAction() {
        this._enableDisableSaveBtn(true);

        // Validate linking and syncing before saving
        const linkingResult = this._model.validateLinking();
        if (!linkingResult.valid) {
            return this._model._muf.displayError({
                errorCode: "MAPPING_ERR",
                errorMsg: linkingResult.errorMsg,
                title: "Error",
            });
        }
        const syncResult = this._model.validateSyncing();
        if (!syncResult.valid) {
            return this._model._muf.displayError({
                errorCode: "MAPPING_ERR",
                errorMsg: syncResult.errorMsg,
                title: "Error",
            });
        }

        // Check if connection is empty
        const connectionId = this._model._snDataSyncConfigEntity.getServiceConnectionId();

        if (!connectionId) {
            // Show confirmation veil/modal
            try {
                await this._model._muf.displayConfirmation(
                    "No connection is selected. Do you want to proceed? This will disable the integration.",
                    {
                        title: "No Connection Selected",
                        confirmDetails: {
                            text: "Save",
                            type: "additive",
                        },
                        rejectDetails: {
                            text: "Cancel",
                            type: "text",
                        },
                    }
                );
                // User confirmed: set enable to false
                this._model._snDataSyncConfigEntity.setEnabled(false);
            } catch (err) {
                if (!err.userRejected) {
                    console.warn(err);
                } else {
                    // User cancelled: do not proceed
                    this._validateCMDBSyncIntegrations();
                    return;
                }
            }
        }

        return this._model
            ._saveEntity()
            .then(() => {
                if (!connectionId) {
                    this._disableEnableTabs(true);
                    this._enableServiceNow.setValue(false);
                }
                this._validateCMDBSyncIntegrations();
            })
            .catch((err) => {
                this.model._logger.warn(err, "ServiceNowViewRenderer._saveButtonAction() error.");
                return Promise.reject(err);
            });
    }

    _cancelButtonAction() {
        this._model.resetAllPatches();
    }

    _clickHandler(e) {
        //if we click delete column
        const target = e.target.closest("base-icon-gl");
        if (
            target &&
            target.getAttribute("role") === "button" &&
            target.dataset.action === "delete" &&
            target.dataset.uuid
        ) {
            const propName = target
                .closest(".ui-widget-content.slick-row")
                .querySelector(".slick-cell.l0.r0").innerText;
            this._deleteDnCLItem(target.dataset.uuid, propName);
        } else if (
            target &&
            target.getAttribute("role") === "button" &&
            target.dataset.action === "delete-sync" &&
            target.dataset.uuid
        ) {
            const propName = target
                .closest(".ui-widget-content.slick-row")
                .querySelector(".slick-cell.l0.r0").innerText;
            this._deleteDnCSItem(target.dataset.uuid, propName);
        } else if (
            target &&
            target.getAttribute("role") === "button" &&
            target.dataset.action === "delete-mapping" &&
            target.dataset.uuid
        ) {
            const propName = target
                .closest(".ui-widget-content.slick-row")
                .querySelector(".slick-cell.l0.r0").innerText;
            this._deleteCnDMItem(target.dataset.uuid, propName);
        }
        this._saveAndCloseEditCellAction(e);
    }

    /*******************************************/
    /* END COMMON EVENT HANDLER METHODS      */
    /* START COMMON TREE METHODS        */
    /*******************************************/

    resetJSTrees() {
        if (this._treeViewForClasses) {
            this._treeViewForClasses.jstree(true).settings.core.data =
                this._model._dataForTreeFiltered;
            this._treeViewForClasses.jstree(true).refresh();
            this._treeViewForClasses.jstree("select_node", "1");
        }

        if (this._treeViewForClasses2) {
            this._treeViewForClasses2.jstree(true).settings.core.data =
                this._model._dataForTreeFiltered;
            this._treeViewForClasses2.jstree(true).refresh();
            this._treeViewForClasses2.jstree("select_node", "1");
        }
    }

    /*******************************************/
    /* END COMMON TREE METHODS                 */
    /* START GENERIC  RENDERER METHODS         */
    /*******************************************/

    _createEmptyBanner(msg, className) {
        const msgContainer = document.createElement("div");
        msgContainer.classList.add(className);
        const innerSpan = document.createElement("span");
        innerSpan.innerText = msg;

        msgContainer.appendChild(innerSpan);

        return msgContainer;
    }

    /*******************************************/
    /* END GENERIC  RENDERER METHODS           */
    /* START GENERIC SLICK GRID METHODS        */
    /*******************************************/

    _saveAndCloseEditCellAction(e) {
        //do nothing if no active editors
        if (this._slickDnCLTable && !this._slickDnCLTable.getEditorLock().isActive()) {
            return false;
        }

        if (this._slickDnCSTable && !this._slickDnCSTable.getEditorLock().isActive()) {
            return false;
        }

        //get event bubbling path
        const path = e.path || e.composedPath();

        //check if click was outside of current editing cell
        let outsideClick = true;
        _.each(path, (element) => {
            const elHasClass = (className) =>
                element.classList && element.classList.contains(className);

            if (elHasClass("slick-cell")) {
                const cellHasActiveEditor = elHasClass("active") && elHasClass("editable");
                outsideClick = !cellHasActiveEditor;
                return false;
            }

            if (element === this._slickTableDnCLContainer) {
                return false;
            }

            if (element === this._slickTableDnCSContainer) {
                return false;
            }

            if (element === this._slickTableDnCSDerivedContainer) {
                return false;
            }
        });

        if (outsideClick) {
            this._slickDnCLTable && this._slickDnCLTable.getEditorLock().commitCurrentEdit();
            this._slickDnCSTable && this._slickDnCSTable.getEditorLock().commitCurrentEdit();
        }
    }

    _registerRowMovePlugin(grid, dataView) {
        const moveRowsPlugin = new Slick.RowMoveManager({
            cancelEditOnDrag: true,
        });
        // const sortingComparator = this._model.CustomFieldsEntity.getSortingComparator("order");

        moveRowsPlugin.onBeforeMoveRows.subscribe(function (e, {insertBefore, rows}) {
            rows = [...rows].sort();
            if (
                _.first(rows) === insertBefore ||
                _.last(rows) === insertBefore - 1
                // _.get(dataView.getItem(insertBefore), "inherited")
            ) {
                e.stopPropagation();
                return false;
            }
            return true;
        });
        moveRowsPlugin.onMoveRows.subscribe((e, args) => {
            let extractedRows = [],
                left,
                right;
            let rows = args.rows;
            let insertBefore = args.insertBefore;
            let data = dataView.getItems();
            left = data.slice(0, insertBefore);
            right = data.slice(insertBefore, data.length);

            rows.sort(function (a, b) {
                return a - b;
            });

            for (let i = 0; i < rows.length; i++) {
                extractedRows.push(data[rows[i]]);
            }

            rows.reverse();

            for (let i = 0; i < rows.length; i++) {
                let row = rows[i];
                if (row < insertBefore) {
                    left.splice(row, 1);
                } else {
                    right.splice(row - insertBefore, 1);
                }
            }

            data = left.concat(extractedRows.concat(right));

            let selectedRows = [];
            for (let i = 0; i < rows.length; i++) {
                selectedRows.push(left.length + i);
            }
            dataView.setItems(data);

            grid.resetActiveCell();
            grid.setSelectedRows(selectedRows);
            grid.invalidateAllRows();
            grid.resizeCanvas();

            this._model._dataForSlickTable = dataView.getItems();

            this._setDnCLInEntityAndEnableDisableActions();
        });

        grid.registerPlugin(moveRowsPlugin);
    }

    _createNoDataPlaceholder() {
        const noDataPlaceholder = document.createElement("div");
        noDataPlaceholder.classList.add("no-data-placeholder");
        noDataPlaceholder.innerText = "No Data";
        return noDataPlaceholder;
    }

    _fitTableSize() {
        if (this._slickDnCLTable) {
            this._slickDnCLTable.resizeCanvas();
        }
    }

    /*******************************************/
    /* END GENERIC SLICK GRID METHODS          */
    /* START CONNECTION TAB                    */
    /*******************************************/

    /* INITIATION : START */

    _createConnectionsTab() {
        this._enableServiceNow = new InputCheckbox({
            label: "Enable ServiceNow CMDB",
            labelShown: true,
            value: false, // Disabled By Default
            darkTheme: true,
        });

        this._onlineIcon = document.createElement("base-icon-gl");
        this._onlineIcon.dataset.icon = "access.ok_with_white_bg";
        this._onlineIcon.setAttribute("title", "Connected");
        this._onlineIcon.classList.add("online-icon");

        this._offlineIcon = document.createElement("base-icon-gl");
        this._offlineIcon.dataset.icon = "access.unreachable_with_white_bg";
        this._offlineIcon.setAttribute("title", "Disconnected");
        this._offlineIcon.classList.add("online-icon");

        this._connectionComboBox = new ComboBox({
            darkTheme: true,
            required: true,
            allowClear: true,
        });

        this._testButton = new Button({
            label: "Test ServiceNow CMDB Connection",
            darkTheme: true,
            disabled: false,
        });

        this._addNewConnectionButton = new Button({
            label: "Add Connection",
            darkTheme: true,
            class: "add-connection-button",
        });

        this._noConnectionEl = document.createElement("div");
        this._noConnectionEl.classList.add("no-cmdb-connection");
        this._connectionsLinkEl = document.createElement("a");
        this._connectionsLinkEl.classList.add("connections-link", "hyperlink");
        this._connectionsLinkEl.innerText = "Connections View";
        this._noConnectionEl.innerHTML =
            "No ServiceNow CMDB Connections available. Please create one in the&nbsp;";
        this._noConnectionEl.appendChild(this._connectionsLinkEl);
        const periodEl = document.createElement("span");
        periodEl.innerText = ".";
        this._noConnectionEl.appendChild(periodEl);
    }

    /* INITIATION : END */

    /* RENDER : START */
    _getConnectionsContent() {
        this._testButton = new Button({
            label: "Test ServiceNow CMDB Connection",
            darkTheme: true,
            disabled: false,
        });

        let _tabMainContent = document.createElement("div");
        _tabMainContent.classList.add("content-main");

        this._connectionsTabContent = document.createElement("div");
        this._connectionsTabContent.classList.add("connection-content");

        this._connectionComboBox.setOptions(this._model._connectionOptions);

        const serviceConnectionSelectionContainer = this._uiUtils.wrapElement(
            this._connectionComboBox.renderer.getRootElement(),
            "Connection",
            "input-container"
        );
        this._connectionsTabContent.appendChild(serviceConnectionSelectionContainer);
        this._connectionsTabContent.appendChild(this._addNewConnectionButton._renderer._rootElem);
        this._connectionsTabContent.appendChild(this._testButton._renderer._rootElem);

        _tabMainContent.appendChild(this._connectionsTabContent);
        _tabMainContent.appendChild(this._noConnectionEl);

        return _tabMainContent;
    }

    _getPristinePropertiesConnectionInfo() {
        return this._model._snDataSyncConfigEntity.pristineProperties.serviceConnectionId;
    }

    _setConnectionValues() {
        const connId = this._getPristinePropertiesConnectionInfo();
        this._connectionComboBox.setValue(connId);
    }

    /* RENDER : END */

    /* EVENTS : Start */

    _valueChange() {
        this._setEntity();
        // this._showHideClientValues(this._authMethod.getValue());
        this._validateCMDBSyncIntegrations();
        this._enableDisableSchedules();
    }

    async _testSSConnectivityAction() {
        try {
            const connectionObj = this._getConnectionInformation();
            await this._model._runTestConnectionWork(connectionObj);
        } catch (err) {
            if (err && err.userRejected) {
                return;
            }
            this.model._logger.warn(
                err,
                "ServiceNowViewRenderer._testSSConnectivityAction() error."
            );
        }
    }

    _handleConnectionsLinkClick(evt) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        this._newConnectionAction();
    }

    async _newConnectionAction() {
        const routeDetails = this._model._router.getRouteDetails();
        routeDetails.sr = "Organization/Connections";

        if (
            this._model._snDataSyncConfigEntity.isSynchronized() &&
            this._model._snDataSyncConfigEntity.isDirty()
        ) {
            try {
                await this._model._muf.displayConfirmation(
                    "You have unsaved changes on this page. If you navigate away, your changes will be lost. Do you want to proceed?",
                    {
                        title: "Unsaved Changes",
                        confirmDetails: {
                            text: "OK",
                            type: "destructive",
                        },
                        rejectDetails: {
                            text: "Cancel",
                            type: "text",
                        },
                    }
                );
                this._model._router.goTo(routeDetails);
            } catch (err) {
                if (!err.userRejected) {
                    console.warn(err);
                }
            }
        } else {
            this._model._router.goTo(routeDetails);
        }
    }

    /* EVENTS : End   */

    /* VALIDATION : START */
    _validateConnectionTab() {
        if (!this._model._snDataSyncConfigEntity.getServiceConnectionId()) {
            return false;
        }
        return true;
    }

    /* VALIDATION : END */

    /* HELPERS : START */

    _getConnectionInformation() {
        const connectionEntity = this._model._serviceConnectionEntities.find(
            (entity) => entity.id === this._connectionComboBox.getValue()
        );
        return connectionEntity;
    }

    _toggleConnectionStatus(isConnected) {
        if (isConnected) {
            this._onlineIcon.classList.add("show");
            this._offlineIcon.classList.remove("show");
        } else {
            this._onlineIcon.classList.remove("show");
            this._offlineIcon.classList.add("show");
        }
    }

    /* HELPERS : END */

    /*******************************************/
    /* END CONNECTION TAB                      */
    /* START SYNCING AND LINKING SCHEDULE      */
    /*******************************************/

    /* INITIATION : START */
    _createLinkingAndSyncingScheduleTab() {
        this._enableDisableSchedulesCheckbox = new InputCheckbox({
            label: "Enable Scheduled Linking and Syncing of Devices with CIs",
            labelShown: true,
            value: false, // Disabled By Default
            darkTheme: true,
        });

        this._scheduleFrequencyComponent = new ScheduleComponent(undefined, {
            allowOne: false,
            allowRepeat: true,
            pollOnly: false,
            availOnly: true,
            disabled: false,
            noStartingEnding: true,
            repeatOption: false,
        });
        this._scheduleFrequencyComponent.theme = "dark";

        this._notificationComponent = new CmdbNotificationComponent(
            this._model._logger,
            this._model._userSession,
            {darkTheme: true, title: "Notify on Completion", workType: null},
            this._model._snDataSyncConfigEntity.getScheduleWorkInput()
        );

        this._previewButton = new Button({
            label: "Preview Linking & Syncing Now",
            darkTheme: true,
        });

        this._runButton = new Button({
            label: "Run Linking & Syncing Now",
            darkTheme: true,
        });

        this._downloadButton = new Button({
            label: "Download Last Report",
            darkTheme: true,
            visible: false,
        });
        this._lastSyncingMsg = document.createElement("span");
        this._lastSyncingMsg.classList.add("last-sync-status");

        this.scheduleButtonContainer = document.createElement("div");
        this.scheduleButtonContainer.classList.add("schedule-buttons-container");

        this.scheduleButtonContainer.appendChild(this._previewButton._renderer._rootElem);
        this.scheduleButtonContainer.appendChild(this._runButton._renderer._rootElem);
        this.scheduleButtonContainer.appendChild(this._downloadButton._renderer._rootElem);
        this.scheduleButtonContainer.appendChild(this._lastSyncingMsg);
    }

    /* INITIATION : END */

    /* RENDER : START */

    _getLinkingSyncingContent() {
        const _tabMainContent = document.createElement("div");
        _tabMainContent.classList.add("content-main");

        this._enableDisableSchedulesCheckbox.setValue(
            this._model._snDataSyncConfigEntity.getScheduleEnabled() || false
        );

        if (!_.isEmpty(this._model._snDataSyncConfigEntity.getSchedule())) {
            this._scheduleFrequencyComponent.setSchedule(
                this._model._snDataSyncConfigEntity.getSchedule() || {}
            );
        } else {
            this._setDefaultSchedule();
        }

        _tabMainContent.appendChild(this._enableDisableSchedulesCheckbox._renderer._rootElem);
        _tabMainContent.appendChild(this._scheduleFrequencyComponent._renderer._rootElem);
        _tabMainContent.appendChild(this._notificationComponent.renderer.rootElement);
        _tabMainContent.appendChild(this.scheduleButtonContainer);
        return _tabMainContent;
    }

    /* RENDER : END */

    /* EVENTS : START */

    _previewWork() {
        this._model.syncWork(true);
    }

    _runWork() {
        this._model.syncWork(false);
    }

    _downloadLastWork() {
        this._model.downloadLastSyncReport();
    }

    _scheduleChangeHandler() {
        this._setEntity();
        this._validateCMDBSyncIntegrations();
    }

    /* EVENTS : END */

    /* VALIDATION : START */
    _validateLinkingSyncingTab() {
        return this._isScheduleValid;
    }

    _onValidationStateChanged(isValid) {
        this._isScheduleValid = isValid;
    }
    /* VALIDATION : END */

    /* HELPERS : START */

    _setDefaultSchedule() {
        this._scheduleFrequencyComponent.setSchedule(this._defaultSchedule, false);
    }

    _getSchedule() {
        if (this._scheduleFrequencyComponent.getSchedule()) {
            return this._scheduleFrequencyComponent.getSchedule();
        } else {
            return this._defaultSchedule;
        }
    }

    _enableDisableSchedules() {
        this._scheduleFrequencyComponent.setDisabledSchedule(
            !this._enableDisableSchedulesCheckbox.getValue()
        );
        // this._previewButton.setDisabled(!this._enableDisableSchedulesCheckbox.getValue());
        // this._runButton.setDisabled(!this._enableDisableSchedulesCheckbox.getValue());
        this._downloadButton.setDisabled(!this._enableDisableSchedulesCheckbox.getValue());
    }

    /* HELPERS : END */

    /*******************************************/
    /* END SYNCING AND LINKING SCHEDULE        */
    /* START DEVICE AND CI LINKING             */
    /*******************************************/

    /* INITIATION : START */
    _createDeviceAndCiLinkingTab() {
        this._onDnCLRowCountChange = this._onDnCLRowCountChange.bind(this);
        this._onDnCLCellChangeAction = this._onDnCLCellChangeAction.bind(this);
        this._onDnCLDerivedCellChangeAction = this._onDnCLDerivedCellChangeAction.bind(this);
        this._onDnCLRowsChanged = this._onDnCLRowsChanged.bind(this);

        this._noDataLinking = this._createEmptyBanner(
            "Unable to connect to ServiceNow CMDB. Please verify configured connection details.",
            "no-cmdb-connection"
        );
        this._noVendorMapLinking = this._createEmptyBanner(
            "No Vendor OS Map available for the Organization.",
            "no-vendor-map"
        );
        this._noMappingLinking = this._createEmptyBanner(
            "No Class and Device Mapping Specified.",
            "no-mapping"
        );

        this._enableNewCI = new InputCheckbox({
            label: "Allow creation of new CIs",
            labelShown: true,
            value: false, // Disabled By Default
            darkTheme: true,
        });

        this._deviceAndCiLinkingContainer = document.createElement("div");
        this._deviceAndCiLinkingContainer.classList.add("device-and-ci-linking-container");
        this._treeClassesCont = document.createElement("div");
        this._treeClassesCont.classList.add("tree-selector");

        const slickTableDnCLContainerMain = document.createElement("slick-table-cmdb-container");

        const slickTableDnCSLabel = document.createElement("h2");
        slickTableDnCSLabel.innerText = "Local Rules";
        const slickTableDnCLDerivedLabel = document.createElement("h2");
        slickTableDnCLDerivedLabel.innerText = "Derived Rules";

        this._addNewRowButton = new Button({
            label: "Add Rule +",
            active: true,
        });

        this._slickTableDnCLContainer = document.createElement("slick-table-cmdb");
        this._slickTableDnCLDerivedContainer = document.createElement("slick-table-cmdb");
        this._slickTableDnCLDerivedContainer.classList.add("derived-rule-container");

        slickTableDnCLContainerMain.appendChild(slickTableDnCSLabel);
        slickTableDnCLContainerMain.appendChild(this._slickTableDnCLContainer);

        const addRowButtonContainer = document.createElement("div");
        addRowButtonContainer.classList.add("add-new-rule-container");
        addRowButtonContainer.appendChild(this._addNewRowButton._renderer._rootElem);
        slickTableDnCLContainerMain.appendChild(addRowButtonContainer);

        slickTableDnCLContainerMain.appendChild(slickTableDnCLDerivedLabel);
        slickTableDnCLContainerMain.appendChild(this._slickTableDnCLDerivedContainer);

        this._slickDnCLTable = undefined;
        this._maxSortOrderDnCL = 0;

        this._slickDnCLDerivedTable = undefined;
        this._maxSortOrderDnCLDerived = 0;

        this._columnsDnCL = [
            {
                id: "order",
                width: 10,
                name: "",
                //field: "order",
                behavior: "selectAndMove",
                asyncPostRender: function (node) {
                    node[0].setAttribute("title", "Reorder");
                },
                selectable: false,
                resizable: false,
                cssClass: "cell-reorder dnd centered g-icon-drag",
                headerCssClass: "centered",
            },
            {
                field: "ignore",
                width: 90,
                name: "Ignored",
                tooltip: "Ignored",
                resizable: false,
                headerCssClass: "centered",
                formatter: formatters.checkboxFormatter,
                editor: checkBoxEditor,
            },
            {
                id: "devicePropPath",
                width: 330,
                name: "Device Property",
                field: "devicePropPath",
                toolTip: "Device Properties",
                resizable: true,
                // formatter: formatters.defaultValueFormatter
                formatter: formatters.fieldFormatter,
                editor: fieldEditor,
            },
            {
                id: "cmdbAttr",
                width: 330,
                name: "CI Attributes",
                field: "cmdbAttr",
                resizable: true,
                toolTip: "CI Attributes",
                formatter: formatters.ciAttributeFormatter,
                editor: SelectBoxEditor,
            },
            {
                id: "deleteRow",
                width: 90,
                name: "Action",
                field: "deleteRow",
                selectable: false,
                resizable: false,
                cssClass: "cell-delete dnd centered",
                formatter: formatters.deleteButtonFormatter,
                headerCssClass: "centered",
            },
        ];

        this._columnsDnCLDerived = [
            {
                id: "order",
                width: 10,
                name: "",
                //field: "order",
                selectable: false,
                resizable: false,
                cssClass: "cell-reorder dnd centered g-icon-drag",
                headerCssClass: "centered",
            },
            {
                field: "ignore",
                width: 90,
                name: "Ignored",
                tooltip: "Ignored",
                resizable: false,
                headerCssClass: "centered",
                formatter: formatters.checkboxFormatter,
                editor: checkBoxEditor,
            },
            {
                id: "devicePropPath",
                width: 330,
                name: "Device Property",
                field: "devicePropPath",
                toolTip: "Device Properties",
                resizable: true,
                // formatter: formatters.defaultValueFormatter
                formatter: formatters.fieldFormatter,
            },
            {
                id: "cmdbAttr",
                width: 330,
                name: "CI Attributes",
                field: "cmdbAttr",
                resizable: true,
                toolTip: "CI Attributes",
                formatter: formatters.ciAttributeFormatter,
            },
        ];

        this._deviceAndCiLinkingContainer.appendChild(this._noMappingLinking);
        this._deviceAndCiLinkingContainer.appendChild(this._noVendorMapLinking);
        this._deviceAndCiLinkingContainer.appendChild(this._noDataLinking);
        this._deviceAndCiLinkingContainer.appendChild(this._treeClassesCont);
        this._deviceAndCiLinkingContainer.appendChild(slickTableDnCLContainerMain);
    }
    /* INITIATION : END */

    /* RENDER - START */
    _getdeviceCILinkingContent() {
        const _tabMainContent = document.createElement("div");
        _tabMainContent.classList.add("content-main", "device-to-ci-linking");

        this._enableNewCI.setValue(this._model._snDataSyncConfigEntity.getCreateCis() || false);
        this._treeViewForClasses = $(this._treeClassesCont).jstree({
            core: {
                data: this._model._dataForTreeFiltered,
                multiple: false, // no multiselection
                themes: {
                    dots: false,
                },
                expand_selected_onload: true,
            },
            types: {
                default: {
                    icon: false,
                },
            },
            search: {
                show_only_matches: true,
                show_only_matches_children: true,
            },
            plugins: ["search", "types"],
        });

        $(this._treeClassesCont).on("changed.jstree", async (e, data) => {
            if (data.node) {
                this._model._selectedClass = data.node.original.value;
                this._userDataStorage.setData(
                    `${data.node.original.id}`,
                    "servicenow-linking-selected-node"
                );
                await this._resetDnCLData(data.node.original.value);
            }
        });

        _tabMainContent.appendChild(this._deviceAndCiLinkingContainer);
        _tabMainContent.appendChild(this._enableNewCI._renderer._rootElem);
        return _tabMainContent;
    }

    _renderDnCLSlickGrid(data) {
        if (!this._slickDnCLTable) {
            this._slickDnCLDataVIew = new Slick.Data.DataView();
            this._slickDnCLDataVIew.setItems(data || []);
            this._slickDnCLTable = new Slick.Grid(
                this._slickTableDnCLContainer,
                this._slickDnCLDataVIew,
                this._columnsDnCL,
                this._tableOptions
            );

            this._registerDnCLSlickTableListeners();
            this._slickDnCLTable.maxSortOrder = this._maxSortOrder;
            this._slickDnCLTable.setSelectionModel(new Slick.RowSelectionModel());
            this._registerRowMovePlugin(this._slickDnCLTable, this._slickDnCLDataVIew);

            this._slickTableDnCLContainer.appendChild(this._createNoDataPlaceholder());
        } else {
            this._resetDnCLData(this._model._selectedClass);
        }
    }

    _renderDnCLDerivedSlickGrid(data) {
        if (!this._slickDnCLDerivedTable) {
            this._slickDnCLDerivedDataVIew = new Slick.Data.DataView();
            this._slickDnCLDerivedDataVIew.setItems(data || []);
            this._slickDnCLDerivedTable = new Slick.Grid(
                this._slickTableDnCLDerivedContainer,
                this._slickDnCLDerivedDataVIew,
                this._columnsDnCLDerived,
                this._tableOptions
            );

            this._registerDnCLDerivedSlickTableListeners();
            this._slickDnCLDerivedTable.maxSortOrder = this._maxSortOrderDnCLDerived;
            this._slickDnCLDerivedTable.setSelectionModel(new Slick.RowSelectionModel());
            this._registerRowMovePlugin(
                this._slickDnCLDerivedTable,
                this._slickDnCLDerivedDataVIew
            );

            this._slickTableDnCLDerivedContainer.appendChild(this._createNoDataPlaceholder());
        } else {
            this._resetDnCLDerivedData(this._model._selectedClassSyncing);
        }
    }

    /* RENDER - END */

    /* HELPERS _ START */
    async _resetDnCLData(selectedTreeNode = undefined) {
        try {
            await this._model._generateSlickDataForInstanceLinking(selectedTreeNode);
            // this.resetDnCLSlickTable();
            return Promise.resolve();
        } catch (err) {
            return Promise.reject(err);
        }
    }

    async _resetDnCLDerivedData(/*selectedTreeNode = undefined*/) {
        //await this._model._dataForSlickTableDerived(selectedTreeNode);
        // this.resetDnCLSlickTable();
    }

    _deleteDnCLItem(id, propName) {
        const deleteConfirmation = () => {
            const deleteMessage = `Are you sure you want to delete '${propName}' from the sync process?`;

            return this._uiUtils.showDeleteConfirmDialog({
                message: deleteMessage,
                confirmButtonName: "Confirm",
                cancelButtonName: "Cancel",
            });
        };

        return deleteConfirmation()
            .then(() => {
                this._model.deleteInstanceLinkingCriteria(id);
                this._slickDnCLDataVIew.deleteItem(id);
                this._model.setInstanceLinking();
                this._validateCMDBSyncIntegrations();
            })
            .catch((error) => error && this._showError(error));
    }

    _setDnCLInEntityAndEnableDisableActions() {
        this._model.setInstanceLinking();
        this._validateCMDBSyncIntegrations();
    }
    /* HELPERS - END */

    /* EVENTS : START */

    _addD2CLinkingClick() {
        this._model.addNewD2CLinkingRule();
    }

    resetDnCLSlickTable() {
        if (this._slickDnCLTable) {
            if (!_.isUndefined(this._model._dataForSlickTable)) {
                this._slickDnCLDataVIew.setItems(this._model._dataForSlickTable);
                this._toggleNoDataPlaceholder(
                    !this._model._dataForSlickTable.length,
                    this._slickTableDnCLContainer
                );
                // this._sortTable();
                this._slickDnCLTable.invalidate();

                this._slickDnCLTable.render();

                this._slickDnCLTable.resizeCanvas();

                this._validateCMDBSyncIntegrations();

                this._errorInLinking(this._model.errLinkingIds);
            }
        } else {
            this._renderDnCLSlickGrid(this._model._dataForSlickTable);
        }

        //now reset derived grid
        this.resetDnCLDerivedSlickTable();
    }

    resetDnCLDerivedSlickTable() {
        if (this._slickDnCLDerivedTable) {
            if (!_.isUndefined(this._model._dataForSlickTableDerived)) {
                this._slickDnCLDerivedDataVIew.setItems(this._model._dataForSlickTableDerived);
                this._toggleNoDataPlaceholder(
                    !this._model._dataForSlickTableDerived.length,
                    this._slickTableDnCLDerivedContainer
                );
                this._slickDnCLDerivedTable.invalidate();

                this._slickDnCLDerivedTable.render();

                this._slickDnCLDerivedTable.resizeCanvas();
            }
        } else {
            this._renderDnCLDerivedSlickGrid(this._model._dataForSlickTableDerived);
        }
    }

    _allowNewCiChange(value) {
        this._model.setAllowCreationOfNewCI(value);
        this._validateCMDBSyncIntegrations();
    }

    _errorInLinking(ids = []) {
        const slickData = this._slickDnCLTable.getData();
        if (slickData.getLength() > 0) {
            ids.forEach((id) => {
                const rowIndex = this._slickDnCLTable.getData().getIdxById(id);
                const currentRow = this._slickDnCLTable.getCanvasNode().children[rowIndex];
                currentRow.classList.add("error-row");
            });
        }
    }

    /* EVENTS : END */

    /* SLICK METHODS - START */

    /* Events - Start */
    _registerDnCLSlickTableListeners() {
        if (this._slickDnCLTable) {
            this._slickDnCLTable.onCellChange.subscribe(this._onDnCLCellChangeAction);
            this._slickDnCLDataVIew.onRowCountChanged.subscribe(this._onDnCLRowCountChange);
            this._slickDnCLDataVIew.onRowsChanged.subscribe(this._onDnCLRowsChanged);
        }
    }

    _registerDnCLDerivedSlickTableListeners() {
        if (this._slickDnCLDerivedTable) {
            this._slickDnCLDerivedTable.onCellChange.subscribe(this._onDnCLDerivedCellChangeAction);
        }
    }

    _unregisterDnCLSlickTableListeners() {
        if (this._slickDnCLTable) {
            this._slickDnCLTable.onCellChange.unsubscribe(this._onDnCLCellChangeAction);
            if (this._slickDnCLDataVIew) {
                this._slickDnCLDataVIew.onRowCountChanged.unsubscribe(this._onDnCLRowCountChange);
                this._slickDnCLDataVIew.onRowsChanged.unsubscribe(this._onDnCLRowsChanged);
            }
        }
    }

    _unregisterDnCLDerivedSlickTableListeners() {
        if (this._slickDnCLDerivedTable) {
            this._slickDnCLDerivedTable.onCellChange.unsubscribe(
                this._onDnCLDerivedCellChangeAction
            );
        }
    }

    _onDnCLRowCountChange(event, {/*previous,*/ current}) {
        if (!this._model._noVendorMap) {
            this._slickDnCLTable.invalidateAllRows();
            this._slickDnCLTable.resizeCanvas();
            this._toggleNoDataPlaceholder(!current, this._slickTableDnCLContainer);
        }
    }
    _onDnCLRowsChanged() {
        this._validateCMDBSyncIntegrations();
    }
    _onDnCLCellChangeAction() {
        this._setDnCLInEntityAndEnableDisableActions();
        this._reapplyLinkingErrorRows();
    }

    _reapplyLinkingErrorRows() {
        if (this._slickDnCLTable) {
            const slickRows = this._slickDnCLTable.getCanvasNode().children;
            for (const currentRow of slickRows) {
                currentRow.classList.remove("error-row");
            }
        }
        if (!_.isEmpty(this._model.errLinkingIds)) {
            this._errorInLinking(this._model.errLinkingIds);
        }
    }

    _onDnCLDerivedCellChangeAction(el, args) {
        this._model.addRemoveD2CLinkingIgnoreRule(args.item);
    }
    /* Events - End */

    /* Helpers - Start */
    _toggleNoDataPlaceholder(state, container) {
        container.classList.toggle("empty", state);
    }

    /* Helpers - End */

    /* SLICK METHODS - END */

    /*******************************************/
    /* END DEVICE AND CI LINKING               */
    /* START DEVICE AND CI SYNCING             */
    /*******************************************/

    /* INITIATION : START */
    _createDeviceAndCiSyncingTab() {
        this._onDnCSRowCountChange = this._onDnCSRowCountChange.bind(this);
        this._onDnCSCellChangeAction = this._onDnCSCellChangeAction.bind(this);
        this._onDnCSDerivedCellChangeAction = this._onDnCSDerivedCellChangeAction.bind(this);
        this._onDnCSRowsChanged = this._onDnCSRowsChanged.bind(this);

        this._noDataSyncing = this._createEmptyBanner(
            "Unable to connect to ServiceNow CMDB. Please verify configured connection details.",
            "no-cmdb-connection"
        );
        this._noVendorMapSyncing = this._createEmptyBanner(
            "No Vendor OS Map available for the Organization.",
            "no-vendor-map"
        );
        this._noMappingSyncing = this._createEmptyBanner(
            "No Class and Device Mapping Specified.",
            "no-mapping"
        );

        this._deviceAndCiSyncingContainer = document.createElement("div");
        this._deviceAndCiSyncingContainer.classList.add("device-and-ci-linking-container");
        this._treeClassesContSyncing = document.createElement("div");
        this._treeClassesContSyncing.classList.add("tree-selector");

        const slickTableDnCSContainerMain = document.createElement("slick-table-cmdb-container");

        const slickTableDnCSLabel = document.createElement("h2");
        slickTableDnCSLabel.innerText = "Local Rules";
        const slickTableDnCSDerivedLabel = document.createElement("h2");
        slickTableDnCSDerivedLabel.innerText = "Derived Rules";

        this._addNewRowDnCSButton = new Button({
            label: "Add Rule +",
            active: true,
        });

        this._slickTableDnCSContainer = document.createElement("slick-table-cmdb");
        this._slickTableDnCSDerivedContainer = document.createElement("slick-table-cmdb");
        this._slickTableDnCSDerivedContainer.classList.add("derived-rule-container");

        slickTableDnCSContainerMain.appendChild(slickTableDnCSLabel);
        slickTableDnCSContainerMain.appendChild(this._slickTableDnCSContainer);

        const addRowButtonDnCSContainer = document.createElement("div");
        addRowButtonDnCSContainer.classList.add("add-new-rule-container");
        addRowButtonDnCSContainer.appendChild(this._addNewRowDnCSButton._renderer._rootElem);
        slickTableDnCSContainerMain.appendChild(addRowButtonDnCSContainer);

        slickTableDnCSContainerMain.appendChild(slickTableDnCSDerivedLabel);
        slickTableDnCSContainerMain.appendChild(this._slickTableDnCSDerivedContainer);

        this._slickDnCSTable = undefined;
        this._maxSortOrderDnCS = 0;

        this._slickDnCSDerivedTable = undefined;
        this._maxSortOrderDnCSDerived = 0;

        this._columnsDnCS = [
            {
                field: "ignore",
                width: 100,
                name: "Ignored",
                tooltip: "Ignored",
                resizable: false,
                headerCssClass: "centered",
                formatter: formatters.checkboxFormatter,
                editor: checkBoxEditor,
            },
            {
                id: "syncDirection",
                width: 310,
                name: "Source",
                field: "syncDirection",
                toolTip: "Source",
                resizable: true,
                formatter: formatters.directionFormatter,
                editor: SourceSelectEditor,
            },
            {
                id: "sourceField",
                width: 350,
                name: "Source Field",
                field: "sourceField",
                resizable: true,
                toolTip: "Source Field",
                formatter: formatters.ciOrDeviceFormatter,
                editor: FieldOrSelectEditor,
            },
            {
                id: "destField",
                width: 350,
                name: "Destination Field",
                field: "destField",
                resizable: true,
                toolTip: "Destination Field",
                formatter: formatters.ciOrDeviceFormatter,
                editor: FieldOrSelectEditor,
            },
            {
                id: "deleteRow",
                width: 90,
                name: "Action",
                field: "deleteRow",
                selectable: false,
                resizable: false,
                cssClass: "cell-delete dnd centered",
                formatter: formatters.deleteButtonFormatter,
                headerCssClass: "centered",
            },
        ];

        this._columnsDnCSDerived = [
            {
                field: "ignore",
                width: 100,
                name: "Ignored",
                tooltip: "Ignored",
                resizable: false,
                headerCssClass: "centered",
                formatter: formatters.checkboxFormatter,
                editor: checkBoxEditor,
            },
            {
                id: "syncDirection",
                width: 310,
                name: "Source",
                field: "syncDirection",
                toolTip: "Source",
                resizable: true,
                formatter: formatters.directionFormatter,
            },
            {
                id: "sourceField",
                width: 350,
                name: "Source Field",
                field: "sourceField",
                resizable: true,
                toolTip: "Source Field",
                formatter: formatters.ciOrDeviceFormatter,
            },
            {
                id: "destField",
                width: 350,
                name: "Destination Field",
                field: "destField",
                resizable: true,
                toolTip: "Destination Field",
                formatter: formatters.ciOrDeviceFormatter,
            },
        ];

        this._deviceAndCiSyncingContainer.appendChild(this._noMappingSyncing);
        this._deviceAndCiSyncingContainer.appendChild(this._noVendorMapSyncing);
        this._deviceAndCiSyncingContainer.appendChild(this._noDataSyncing);
        this._deviceAndCiSyncingContainer.appendChild(this._treeClassesContSyncing);
        this._deviceAndCiSyncingContainer.appendChild(slickTableDnCSContainerMain);
    }
    /* INITIATION : END */

    /* RENDERING - START */
    _getdeviceCISyncingContent() {
        const _tabMainContent = document.createElement("div");
        _tabMainContent.classList.add("content-main", "device-to-ci-linking");

        this._treeViewForClasses2 = $(this._treeClassesContSyncing).jstree({
            core: {
                data: this._model._dataForTreeFiltered,
                multiple: false, // no multiselection
                themes: {
                    dots: false,
                },
                expand_selected_onload: true,
            },
            types: {
                default: {
                    icon: false,
                },
            },
            search: {
                show_only_matches: true,
                show_only_matches_children: true,
            },
            plugins: ["search", "types"],
        });

        $(this._treeClassesContSyncing).on("changed.jstree", async (e, data) => {
            if (data.node) {
                this._model._selectedClassSyncing = data.node.original.value;
                this._userDataStorage.setData(
                    `${data.node.original.id}`,
                    "servicenow-syncing-selected-node"
                );
                await this._resetDnCSData(data.node.original.value);
            }
        });

        _tabMainContent.appendChild(this._deviceAndCiSyncingContainer);
        return _tabMainContent;
    }

    _renderDnCSSlickGrid(data) {
        if (!this._slickDnCSTable) {
            this._slickDnCSDataVIew = new Slick.Data.DataView();
            this._slickDnCSDataVIew.setItems(data || []);
            this._slickDnCSTable = new Slick.Grid(
                this._slickTableDnCSContainer,
                this._slickDnCSDataVIew,
                this._columnsDnCS,
                this._tableOptions
            );

            this._registerDnCSSlickTableListeners();
            this._slickDnCSTable.maxSortOrder = this._maxSortOrderDnCS;
            this._slickDnCSTable.setSelectionModel(new Slick.RowSelectionModel());
            this._registerRowMovePlugin(this._slickDnCSTable, this._slickDnCSDataVIew);

            this._slickTableDnCSContainer.appendChild(this._createNoDataPlaceholder());
        } else {
            this._resetDnCSData(this._model._selectedClassSyncing);
        }
    }

    _renderDnCSDerivedSlickGrid(data) {
        if (!this._slickDnCSDerivedTable) {
            this._slickDnCSDerivedDataVIew = new Slick.Data.DataView();
            this._slickDnCSDerivedDataVIew.setItems(data || []);
            this._slickDnCSDerivedTable = new Slick.Grid(
                this._slickTableDnCSDerivedContainer,
                this._slickDnCSDerivedDataVIew,
                this._columnsDnCSDerived,
                this._tableOptions
            );

            this._registerDnCSDerivedSlickTableListeners();
            this._slickDnCSDerivedTable.maxSortOrder = this._maxSortOrderDnCSDerived;
            this._slickDnCSDerivedTable.setSelectionModel(new Slick.RowSelectionModel());
            this._registerRowMovePlugin(
                this._slickDnCSDerivedTable,
                this._slickDnCSDerivedDataVIew
            );

            this._slickTableDnCSDerivedContainer.appendChild(this._createNoDataPlaceholder());
        } else {
            this._resetDnCSDerivedData(this._model._selectedClassSyncing);
        }
    }
    /* RENDERING - START */

    /* EVENTS - START */

    resetDnCSSlickTable() {
        if (this._slickDnCSTable) {
            if (!_.isUndefined(this._model._dataForSlickTableSyncing)) {
                this._slickDnCSDataVIew.setItems(this._model._dataForSlickTableSyncing);
                // this._sortTable();
                this._toggleNoDataPlaceholder(
                    !this._model._dataForSlickTableSyncing.length,
                    this._slickTableDnCSContainer
                );
                this._slickDnCSTable.invalidate();

                this._slickDnCSTable.render();

                this._slickDnCSTable.resizeCanvas();

                this._validateCMDBSyncIntegrations();

                this._errorInSyncing(this._model.errSyncingIds);
            }
        } else {
            this._renderDnCSSlickGrid(this._model._dataForSlickTableSyncing);
        }
        //Now reset Derived table
        this.resetDnCSDerivedSlickTable();
    }

    resetDnCSDerivedSlickTable() {
        if (this._slickDnCSDerivedTable) {
            if (!_.isUndefined(this._model._dataForSlickTableDerivedSyncing)) {
                this._slickDnCSDerivedDataVIew.setItems(
                    this._model._dataForSlickTableDerivedSyncing
                );
                this._toggleNoDataPlaceholder(
                    !this._model._dataForSlickTableDerivedSyncing.length,
                    this._slickTableDnCSDerivedContainer
                );
                this._slickDnCSDerivedTable.invalidate();

                this._slickDnCSDerivedTable.render();

                this._slickDnCSDerivedTable.resizeCanvas();

                //this._validateCMDBSyncIntegrations();

                this._errorInSyncing(this._model.errSyncingIds);
            }
        } else {
            this._renderDnCSDerivedSlickGrid(this._model._dataForSlickTableDerivedSyncing);
        }
    }

    _addD2CSyncingClick() {
        this._model.addNewD2CSyncingRule();
    }

    /* EVENTS - End */

    /* HELPERS - START */

    _errorInSyncing(ids = []) {
        ids.forEach((id) => {
            const rowIndex = this._slickDnCSTable.getData().getIdxById(id);
            const currentRow = this._slickDnCSTable.getCanvasNode().children[rowIndex];
            currentRow.classList.add("error-row");
        });
    }

    async _resetDnCSData(selectedTreeNode = undefined) {
        await this._model._generateSlickDataForInstanceSyncing(selectedTreeNode);
        // this.resetDnCLSlickTable();
    }

    async _resetDnCSDerivedData(/*selectedTreeNode = undefined*/) {
        //await this._model._dataForSlickTableDerivedSyncing(selectedTreeNode);
        // this.resetDnCLSlickTable();
    }

    _deleteDnCSItem(id, propName) {
        const deleteConfirmation = () => {
            const deleteMessage = `Are you sure you want to delete '${propName}' from the sync process?`;

            return this._uiUtils.showDeleteConfirmDialog({
                message: deleteMessage,
                confirmButtonName: "Confirm",
                cancelButtonName: "Cancel",
            });
        };

        return deleteConfirmation()
            .then(() => {
                this._model.deleteInstanceSyncingCriteria(id);
                this._slickDnCSDataVIew.deleteItem(id);
                this._model.setDataSyncMappings();
                this._validateCMDBSyncIntegrations();
            })
            .catch((error) => error && this._showError(error));
    }

    /* HELPERS - END */

    /* SLICK METHODS - START */

    /* Events - Start */
    _registerDnCSSlickTableListeners() {
        if (this._slickDnCSTable) {
            this._slickDnCSTable.onCellChange.subscribe(this._onDnCSCellChangeAction);
            this._slickDnCSDataVIew.onRowCountChanged.subscribe(this._onDnCSRowCountChange);
            this._slickDnCSDataVIew.onRowsChanged.subscribe(this._onDnCSRowsChanged);
        }
    }

    _registerDnCSDerivedSlickTableListeners() {
        if (this._slickDnCSDerivedTable) {
            this._slickDnCSDerivedTable.onCellChange.subscribe(this._onDnCSDerivedCellChangeAction);
        }
    }

    _unregisterDnCSSlickTableListeners() {
        if (this._slickDnCSTable) {
            this._slickDnCSTable.onCellChange.unsubscribe(this._onDnCSCellChangeAction);
            if (this._slickDnCSDataVIew) {
                this._slickDnCSDataVIew.onRowCountChanged.unsubscribe(this._onDnCSRowCountChange);
                this._slickDnCSDataVIew.onRowsChanged.unsubscribe(this._onDnCSRowsChanged);
            }
        }
    }

    _unregisterDnCSDerivedSlickTableListeners() {
        if (this._slickDnCSDerivedTable) {
            this._slickDnCSDerivedTable.onCellChange.unsubscribe(
                this._onDnCSDerivedCellChangeAction
            );
        }
    }

    _onDnCSRowCountChange(event, {/*previous,*/ current}) {
        if (!this._model._noVendorMap) {
            this._slickDnCSTable.invalidateAllRows();
            this._slickDnCSTable.resizeCanvas();
            this._toggleNoDataPlaceholder(!current, this._slickTableDnCSContainer); // Create new no data holder
        }
    }
    _onDnCSRowsChanged() {
        this._validateCMDBSyncIntegrations();
    }
    _onDnCSCellChangeAction() {
        this._setDnCSInEntityAndEnableDisableActions();
        this._reapplySyncingErrorRows();
    }

    _reapplySyncingErrorRows() {
        if (this._slickDnCSTable) {
            const slickRows = this._slickDnCSTable.getCanvasNode().children;
            for (const currentRow of slickRows) {
                currentRow.classList.remove("error-row");
            }
        }
        if (!_.isEmpty(this._model.errSyncingIds)) {
            this._errorInSyncing(this._model.errSyncingIds);
        }
    }

    _onDnCSDerivedCellChangeAction(el, args) {
        this._model.addRemoveD2CSyncingIgnoreRule(args.item);
    }
    /* Events - End */

    /* Helpers - Start */

    _setDnCSInEntityAndEnableDisableActions() {
        this._model.setDataSyncMappings();
        this._validateCMDBSyncIntegrations();
    }

    /* Helpers - End */

    /* SLICK METHODS - END */

    /*******************************************/
    /* END DEVICE AND CI SYNCING               */
    /* START CLASS AND DEVICE MAPPING          */
    /*******************************************/

    /* INITIATION START */

    _createClassAndDeviceMappingTab() {
        this._onCnDMRowCountChange = this._onCnDMRowCountChange.bind(this);
        this._onCnDMCellChangeAction = this._onCnDMCellChangeAction.bind(this);
        this._onCnDMRowsChanged = this._onCnDMRowsChanged.bind(this);

        this._noDataMapping = this._createEmptyBanner(
            "Unable to connect to ServiceNow CMDB. Please verify configured connection details.",
            "no-cmdb-connection"
        );
        this._noVendorMapMapping = this._createEmptyBanner(
            "No Vendor OS Map available for the Organization.",
            "no-vendor-map"
        );

        this._classAndDeviceMappingContainer = document.createElement("div");
        this._classAndDeviceMappingContainer.classList.add("class-and-device-mapping-container");
        this._classAndDeviceMappingContainer.classList.add("device-and-ci-linking-container");

        const slickTableCnDMContainerMain = document.createElement("slick-table-cmdb-container");

        this._addNewRowCnDMButton = new Button({
            label: "Add Class +",
            active: true,
        });

        this._slickTableCnDMContainer = document.createElement("slick-table-cmdb");

        slickTableCnDMContainerMain.appendChild(this._slickTableCnDMContainer);

        const addRowButtonDnCSContainer = document.createElement("div");
        addRowButtonDnCSContainer.classList.add("add-new-rule-container");
        addRowButtonDnCSContainer.appendChild(this._addNewRowCnDMButton._renderer._rootElem);
        slickTableCnDMContainerMain.appendChild(addRowButtonDnCSContainer);

        this._slickCnDMTable = undefined;
        this._maxSortOrderCnDM = 0;

        this._columnsCnDM = [
            {
                id: "cmdbClass",
                width: 650,
                name: "Class Name",
                field: "cmdbClass",
                toolTip: "Class Name",
                resizable: true,
                formatter: formatters.classNameFormatter,
                editor: ClassEditor,
            },
            {
                id: "deviceBaseType",
                width: 350,
                name: "Type",
                field: "deviceBaseType",
                resizable: true,
                toolTip: "Type",
                formatter: formatters.deviceTypeBaseFormatter,
                editor: SelectTypeEditor,
            },
            {
                id: "deviceVendor",
                width: 200,
                name: "Vendor",
                field: "deviceVendor",
                resizable: true,
                toolTip: "Vendor",
                formatter: formatters.vendorFormatter,
                editor: SelectTypeEditor,
            },
            {
                id: "deviceOs",
                width: 200,
                name: "OS",
                field: "deviceOs",
                resizable: true,
                toolTip: "OS",
                formatter: formatters.osFormatter,
                editor: SelectTypeEditor,
            },
            {
                id: "deleteRow",
                width: 50,
                name: "Action",
                field: "deleteRow",
                selectable: false,
                resizable: false,
                cssClass: "cell-delete dnd centered",
                formatter: formatters.deleteButtonFormatter,
                headerCssClass: "centered",
            },
        ];

        this._classAndDeviceMappingContainer.appendChild(this._noDataMapping);
        this._classAndDeviceMappingContainer.appendChild(this._noVendorMapMapping);
        this._classAndDeviceMappingContainer.appendChild(slickTableCnDMContainerMain);
    }
    /* INITIATION END */

    /* RENDERING START */
    /**
     *
     * @returns Content for Device and Class mapping Tab
     */
    _getclassNDeviceMappingContent() {
        const _tabMainContent = document.createElement("div");
        _tabMainContent.classList.add(
            "content-main",
            "device-to-ci-linking",
            "device-and-ci-mapping"
        );

        // this._renderCnDMSlickGrid();

        _tabMainContent.appendChild(this._classAndDeviceMappingContainer);
        return _tabMainContent;
    }

    _renderCnDMSlickGrid(data) {
        if (!this._slickCnDMTable) {
            this._slickCnDMDataVIew = new Slick.Data.DataView();
            this._slickCnDMDataVIew.setItems(data || []);
            this._slickCnDMTable = new Slick.Grid(
                this._slickTableCnDMContainer,
                this._slickCnDMDataVIew,
                this._columnsCnDM,
                this._tableOptions
            );

            this._registerCnDMSlickTableListeners();
            this._slickCnDMTable.resizeCanvas();

            this._slickTableCnDMContainer.appendChild(this._createNoDataPlaceholder());
        }
    }

    /* RENDERING END */

    /* HELPERS START */

    async _resetCnDMData() {
        await this._model._generateSlickDataForClassAndDeviceMapping();
    }

    /* HELPERS END */

    /* EVENTS START */

    resetCnDMSlickTable() {
        if (this._slickCnDMTable) {
            if (!_.isUndefined(this._model._dataForSlickTableMapping)) {
                this._slickCnDMDataVIew.setItems(this._model._dataForSlickTableMapping);
                // this._sortTable();
                this._toggleNoDataPlaceholder(
                    !this._model._dataForSlickTableMapping.length,
                    this._slickTableCnDMContainer
                );

                this._slickCnDMTable.invalidate();

                this._slickCnDMTable.render();

                this._slickCnDMTable.resizeCanvas();

                this._validateCMDBSyncIntegrations();

                // this._errorInSyncing(this._model.errMappingIds);
            }
        } else {
            this._renderCnDMSlickGrid(this._model._dataForSlickTableMapping);
        }
    }

    async _addCnDMappingClick() {
        this._model.addNewCnDMappingRule();

        await this._model._loadSelectedClasses();
        this.resetJSTrees();
    }

    _registerCnDMSlickTableListeners() {
        if (this._slickCnDMTable) {
            this._slickCnDMTable.onCellChange.subscribe(this._onCnDMCellChangeAction);
            this._slickCnDMDataVIew.onRowCountChanged.subscribe(this._onCnDMRowCountChange);
            this._slickCnDMDataVIew.onRowsChanged.subscribe(this._onCnDMRowsChanged);
        }
    }

    _unregisterCnDMSlickTableListeners() {
        if (this._slickCnDMTable) {
            this._slickCnDMTable.onCellChange.unsubscribe(this._onCnDMCellChangeAction);
            if (this._slickCnDMDataVIew) {
                this._slickCnDMDataVIew.onRowCountChanged.unsubscribe(this._onCnDMRowCountChange);
                this._slickCnDMDataVIew.onRowsChanged.unsubscribe(this._onCnDMRowsChanged);
            }
        }
    }

    _onCnDMRowCountChange(event, {/*previous,*/ current}) {
        if (!this._model._noVendorMap) {
            this._slickCnDMTable.invalidateAllRows();
            this._slickCnDMTable.resizeCanvas();
            this._toggleNoDataPlaceholder(!current, this._slickTableCnDMContainer); // Create new no data holder
        }
    }
    _onCnDMRowsChanged() {
        this._validateCMDBSyncIntegrations();
    }
    _onCnDMCellChangeAction() {
        const slickRows = this._slickCnDMTable.getCanvasNode().children;
        for (let currentRow of slickRows) {
            currentRow.classList.remove("error-row");
        }

        this._setCnDMInEntityAndEnableDisableActions();
    }

    /* EVENTS END */

    /* HELPERS START */

    async _setCnDMInEntityAndEnableDisableActions() {
        this._model.setNamespaceMatchingCriteria();
        this._validateCMDBSyncIntegrations();

        await this._model._loadSelectedClasses();
        this.resetJSTrees();
    }

    _deleteCnDMItem(id, propName) {
        const deleteConfirmation = () => {
            const deleteMessage = `Are you sure you want to delete '${propName}' from the sync process?`;

            return this._uiUtils.showDeleteConfirmDialog({
                message: deleteMessage,
                confirmButtonName: "Confirm",
                cancelButtonName: "Cancel",
            });
        };

        return deleteConfirmation()
            .then(() => {
                this._model.deleteNamespaceMatchingCriteria(id);
                this._slickCnDMDataVIew.deleteItem(id);
                this._model.setNamespaceMatchingCriteria();
                this._validateCMDBSyncIntegrations();

                return this._model._loadSelectedClasses();
            })
            .then(() => {
                this.resetJSTrees();
            })
            .catch((error) => error && this._showError(error));
    }

    /* HELPERS END */

    /*******************************************/
    /* END CLASS AND DEVICE MAPPING            */
    /*******************************************/
}

module.exports = ServiceNowViewRenderer;
